import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import Cookies from "js-cookie";
import { getDashboardForRole, getStoredUser } from "./components/config/authUtils";

// Public Routes
import Login from "./components/auth/Login";
import Lockscreen from "./components/auth/Lockscreen";

// HR Admin / Manager Routes
import Dashboard from "./components/hr manager/screens/main/Dashboard";
import Departments from "./components/hr manager/screens/main/Departments";
import CreateDepartment from "./components/hr manager/screens/main/CreateDepartment";
import CostCenters from "./components/hr manager/screens/main/CostCenters";
import CreateCostCenter from "./components/hr manager/screens/main/CreateCostCenter";
import CreateEmployee from "./components/hr manager/screens/main/CreateEmployee";
import UpdateEmployee from "./components/hr manager/screens/main/UpdateEmployee";
import Employees from "./components/hr manager/screens/main/Employees";
import HREmployees from "./components/hr manager/screens/main/HREmployees";
import AllEmployeesGoalsReview from "./components/hr manager/screens/main/AllEmployeesGoalsReview";
import HRAddKRA from "./components/hr manager/screens/main/HRAddKRA";
import HRKRAs from "./components/hr manager/screens/main/HRKRAs";
import HRCreateGoal from "./components/hr manager/screens/main/HRCreateGoal";
import HRGoals from "./components/hr manager/screens/main/HRGoals";
import MyGoalReviews from "./components/hr manager/screens/main/MyGoalReview";
import HRImportKPIs from "./components/hr manager/screens/main/HRImportKPIs";
import HRCreateImprovementPlan from "./components/hr manager/screens/main/HRCreateImprovementPlan";
import HRImprovementPlans from "./components/hr manager/screens/main/HRImprovementPlans";
import HREditIP from "./components/hr manager/screens/main/HREditIP";
import HRProfile from "./components/hr manager/screens/main/HRProfile";
import HRChangePassword from "./components/hr manager/screens/main/HRChangePassword";
import EmployeeGoals from "./components/hr manager/screens/main/EmployeeGoals";
import HREmployeeGoals from "./components/hr manager/screens/main/HREmployeeGoals";
import HREmployeesGoals from "./components/hr manager/screens/main/HREmployeesGoals";
import HREmployeesPendingGoals from "./components/hr manager/screens/main/HREmployeesPendingGoals";
import HREmployeeGoalsReview from "./components/hr manager/screens/main/HREmployeeGoalsReview";
import DepartmentEmployeesImprovementPlans from "./components/hr manager/screens/main/DepartmentEmployeesImprovementPlans";
import Analytics from "./components/hr manager/screens/main/Analytics";
import EmployeeReport from "./components/hr manager/screens/main/EmployeeReport";

// COS Routes
import COSDashboard from "./components/cos/screens/main/COSDashboard";
import COSChangePassword from "./components/cos/screens/main/COSChangePassword";
import COSCreateEmployee from "./components/cos/screens/main/COSCreateEmployee";
import COSEditEmployee from "./components/cos/screens/main/COSEditEmployee";
import COSEmployees from "./components/cos/screens/main/COSEmployees";
import COSAddKRA from "./components/cos/screens/main/COSAddKRA";
import COSKRAs from "./components/cos/screens/main/COSKRAs";
import COSCreateGoal from "./components/cos/screens/main/COSCreateGoal";
import COSGoals from "./components/cos/screens/main/COSGoals";
import COSGoalsReview from "./components/cos/screens/main/COSGoalsReview";
import COSPendingGoals from "./components/cos/screens/main/COSPendingGoals";
import COSEmployeeGoals from "./components/cos/screens/main/COSEmployeeGoals";
import COSEmployeeGoalsReview from "./components/cos/screens/main/COSEmployeeGoalsReview";
import COSCreateImprovementPlan from "./components/cos/screens/main/COSCreateImprovementPlan";
import COSImprovementPlans from "./components/cos/screens/main/COSImprovementPlans";
import COSEditIP from "./components/cos/screens/main/COSEditIP";
import HODsImprovementPlans from "./components/cos/screens/main/HODsImprovementPlans";
import HODsReport from "./components/cos/screens/main/HODsReport";

const resolveRoleDashboard = () => {
  const storedUser = getStoredUser();
  const role =
    Cookies.get("role") ||
    storedUser?.role ||
    storedUser?.role_display;
  return getDashboardForRole(role) || "/login";
};

const ProtectedRoute = ({ children }) => {
  const isAuthenticated = !!Cookies.get("token");
  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

const PublicRoute = ({ children }) => {
  const isAuthenticated = !!Cookies.get("token");
  return !isAuthenticated ? (
    children
  ) : (
    <Navigate to={resolveRoleDashboard()} replace />
  );
};

const RoleRedirect = () => {
  const isAuthenticated = !!Cookies.get("token");
  return isAuthenticated ? (
    <Navigate to={resolveRoleDashboard()} replace />
  ) : (
    <Navigate to="/login" replace />
  );
};

function App() {
  return (
    <div className="App">
      <Router>
        <Routes>
          {/* Root: redirect to login or role-specific dashboard */}
          <Route path="/" element={<RoleRedirect />} />

          {/* Public Routes */}
          <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
          <Route path="/lockscreen" element={<PublicRoute><Lockscreen /></PublicRoute>} />

          {/* HR Dashboard */}
          <Route path="/hr/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/reports" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />

          {/* Departments */}
          <Route path="/hr/department/add" element={<ProtectedRoute><CreateDepartment /></ProtectedRoute>} />
          <Route path="/hr/departments/add" element={<ProtectedRoute><CreateDepartment /></ProtectedRoute>} />
          <Route path="/hr/departments" element={<ProtectedRoute><Departments /></ProtectedRoute>} />

          {/* Cost Centers */}
          <Route path="/hr/cost-centers/add" element={<ProtectedRoute><CreateCostCenter /></ProtectedRoute>} />
          <Route path="/hr/cost-centers" element={<ProtectedRoute><CostCenters /></ProtectedRoute>} />

          {/* Employees */}
          <Route path="/hr/employees/add" element={<ProtectedRoute><CreateEmployee /></ProtectedRoute>} />
          <Route path="/hr/employees/update/:id" element={<ProtectedRoute><UpdateEmployee /></ProtectedRoute>} />
          <Route path="/hr/employees" element={<ProtectedRoute><HREmployees /></ProtectedRoute>} />
          <Route path="/hr/all-employees" element={<ProtectedRoute><Employees /></ProtectedRoute>} />
          <Route path="/hr/department-employees" element={<ProtectedRoute><HREmployees /></ProtectedRoute>} />
          <Route path="/hr/employee/:id/update" element={<ProtectedRoute><UpdateEmployee /></ProtectedRoute>} />

          {/* Employee Goal & KPI Management */}
          <Route path="/hr/employee-goals/:id" element={<ProtectedRoute><HREmployeesGoals /></ProtectedRoute>} />
          <Route path="/hr/employee/:id/goals" element={<ProtectedRoute><EmployeeGoals /></ProtectedRoute>} />
          <Route path="/hr/employee-goals/:id/reviews" element={<ProtectedRoute><HREmployeeGoalsReview /></ProtectedRoute>} />
          <Route path="/hr/employee-goals-review/:id" element={<ProtectedRoute><HREmployeeGoalsReview /></ProtectedRoute>} />
          <Route path="/hr/employee/:id/reviews" element={<ProtectedRoute><AllEmployeesGoalsReview /></ProtectedRoute>} />
          <Route path="/hr/employees-pending-goals" element={<ProtectedRoute><HREmployeesPendingGoals /></ProtectedRoute>} />
          <Route path="/hr/department-employees-improvement-plans" element={<ProtectedRoute><DepartmentEmployeesImprovementPlans /></ProtectedRoute>} />
          <Route path="/hr/department-employee/:id/goals" element={<ProtectedRoute><HREmployeeGoals /></ProtectedRoute>} />
          <Route path="/hr/employee/:employeeId/report" element={<ProtectedRoute><EmployeeReport /></ProtectedRoute>} />

          {/* Personal Key Result Areas (KRAs) */}
          <Route path="/hr/key-result-areas/add" element={<ProtectedRoute><HRAddKRA /></ProtectedRoute>} />
          <Route path="/hr/key-result-areas" element={<ProtectedRoute><HRKRAs /></ProtectedRoute>} />

          {/* Personal KPIs */}
          <Route path="/hr/kpis/add" element={<ProtectedRoute><HRCreateGoal /></ProtectedRoute>} />
          <Route path="/hr/kpis" element={<ProtectedRoute><HRGoals /></ProtectedRoute>} />
          <Route path="/hr/kpis/import" element={<ProtectedRoute><HRImportKPIs /></ProtectedRoute>} />
          <Route path="/hr/kpi-reviews" element={<ProtectedRoute><MyGoalReviews /></ProtectedRoute>} />
          <Route path="/hr/my-goal-reviews" element={<ProtectedRoute><MyGoalReviews /></ProtectedRoute>} />

          {/* Personal Improvement Plans */}
          <Route path="/hr/improvement-plans/add" element={<ProtectedRoute><HRCreateImprovementPlan /></ProtectedRoute>} />
          <Route path="/hr/create-improvement-plan" element={<ProtectedRoute><HRCreateImprovementPlan /></ProtectedRoute>} />
          <Route path="/hr/improvement-plans" element={<ProtectedRoute><HRImprovementPlans /></ProtectedRoute>} />
          <Route path="/hr/edit-improvement-plan/:id" element={<ProtectedRoute><HREditIP /></ProtectedRoute>} />

          {/* Profile & Account (HR) */}
          <Route path="/hr/profile" element={<ProtectedRoute><HRProfile /></ProtectedRoute>} />
          <Route path="/hr/change-password" element={<ProtectedRoute><HRChangePassword /></ProtectedRoute>} />

          {/* COS Routes */}
          <Route path="/cos/dashboard" element={<ProtectedRoute><COSDashboard /></ProtectedRoute>} />
          <Route path="/cos/change-password" element={<ProtectedRoute><COSChangePassword /></ProtectedRoute>} />

          <Route path="/cos/employees" element={<ProtectedRoute><COSEmployees /></ProtectedRoute>} />
          <Route path="/cos/employees/add" element={<ProtectedRoute><COSCreateEmployee /></ProtectedRoute>} />
          <Route path="/cos/employees/update/:id" element={<ProtectedRoute><COSEditEmployee /></ProtectedRoute>} />
          <Route path="/cos/employee/:id/update" element={<ProtectedRoute><COSEditEmployee /></ProtectedRoute>} />

          <Route path="/cos/key-result-areas" element={<ProtectedRoute><COSKRAs /></ProtectedRoute>} />
          <Route path="/cos/create-key-result-area" element={<ProtectedRoute><COSAddKRA /></ProtectedRoute>} />

          <Route path="/cos/kpis" element={<ProtectedRoute><COSGoals /></ProtectedRoute>} />
          <Route path="/cos/kpis/add" element={<ProtectedRoute><COSCreateGoal /></ProtectedRoute>} />
          <Route path="/cos/create-goal" element={<ProtectedRoute><COSCreateGoal /></ProtectedRoute>} />
          <Route path="/cos/kpi-reviews" element={<ProtectedRoute><COSGoalsReview /></ProtectedRoute>} />
          <Route path="/cos/my-goal-reviews" element={<ProtectedRoute><COSGoalsReview /></ProtectedRoute>} />
          <Route path="/cos/my-goals-reviews" element={<ProtectedRoute><COSGoalsReview /></ProtectedRoute>} />

          <Route path="/cos/pending-goals" element={<ProtectedRoute><COSPendingGoals /></ProtectedRoute>} />
          <Route path="/cos/employee-goals/:id" element={<ProtectedRoute><COSEmployeeGoals /></ProtectedRoute>} />
          <Route path="/cos/employee/:id/goals" element={<ProtectedRoute><COSEmployeeGoals /></ProtectedRoute>} />
          <Route path="/cos/employee-goals/:id/reviews" element={<ProtectedRoute><COSEmployeeGoalsReview /></ProtectedRoute>} />
          <Route path="/cos/employee-goals-review/:id" element={<ProtectedRoute><COSEmployeeGoalsReview /></ProtectedRoute>} />

          <Route path="/cos/improvement-plans" element={<ProtectedRoute><COSImprovementPlans /></ProtectedRoute>} />
          <Route path="/cos/improvement-plans/add" element={<ProtectedRoute><COSCreateImprovementPlan /></ProtectedRoute>} />
          <Route path="/cos/create-improvement-plan" element={<ProtectedRoute><COSCreateImprovementPlan /></ProtectedRoute>} />
          <Route path="/cos/edit-improvement-plan/:id" element={<ProtectedRoute><COSEditIP /></ProtectedRoute>} />
          <Route path="/cos/hods-improvement-plans" element={<ProtectedRoute><HODsImprovementPlans /></ProtectedRoute>} />
          <Route path="/cos/hod/:employeeId/report" element={<ProtectedRoute><HODsReport /></ProtectedRoute>} />

          {/* Fallback: unknown route → login (unauth) or role dashboard (auth) */}
          <Route path="*" element={<RoleRedirect />} />
        </Routes>
      </Router>
    </div>
  );
}

export default App;
